import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import '../GiftCardForm.css';

const TAG_PALETTE = [
    { bg: '#F5EEFF', text: '#7B4B9E', border: '#D4B8F0' },
    { bg: '#FFF8E5', text: '#AA7000', border: '#FFD97A' },
    { bg: '#E8F8F0', text: '#2D7D56', border: '#98DDB8' },
    { bg: '#FFF0F1', text: '#CC3344', border: '#F5A3AB' },
    { bg: '#E8F4FE', text: '#1A6FA8', border: '#90CCF0' },
    { bg: '#FFF4E8', text: '#A85200', border: '#FFBF7A' },
    { bg: '#F0EFFF', text: '#4A44AA', border: '#B8B6F0' },
    { bg: '#F0FFF8', text: '#1A7A5A', border: '#8AE0C0' },
];

function slugColor(slug) {
    let h = 0;
    for (let i = 0; i < slug.length; i++) h = slug.charCodeAt(i) + ((h << 5) - h);
    return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length];
}

export default function CreateGIftCardImage() {
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const { register, handleSubmit, watch, setValue } = useForm({
        defaultValues: { language_code: 'da', currency_code: 'DKK', is_active: 1 },
    });

    const [imagePreview, setImagePreview] = useState(null);
    const [imageBase64, setImageBase64]   = useState(null);
    const [selectedTags, setSelectedTags] = useState([]);
    const [menuOpen, setMenuOpen]         = useState(false);
    const menuRef = useRef(null);

    const { data: catRes } = useQuery({
        queryKey: ['gift-cat-form'],
        queryFn: () => axiosPrivate.get('gift-category/all'),
    });
    const categories = catRes?.data?.data || [];

    const isActive = watch('is_active');
    const language = watch('language_code');
    const currency = watch('currency_code');

    // Close dropdown on outside click
    useEffect(() => {
        const fn = e => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', fn);
        return () => document.removeEventListener('mousedown', fn);
    }, []);

    const handleFile = e => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10_000_000) { alert('File must be under 10 MB'); return; }
        const reader = new FileReader();
        reader.onload = () => { setImageBase64(reader.result); setImagePreview(reader.result); };
        reader.readAsDataURL(file);
    };

    const postData = async data => axiosPrivate.post('gift-image/create-new', {
        title: data.title,
        is_active: data.is_active,
        image: imageBase64 || null,
        price: data.price,
        country_code: 'dk',
        currency_code: data.currency_code,
        language_code: data.language_code,
        tags: selectedTags.join(','),
    });

    const { mutate, isLoading: saving } = useMutation(postData, {
        onSuccess: res => {
            if (res?.data?.success === false) {
                Swal.fire('Failed', res?.data?.message || 'Something went wrong.', 'error');
            } else {
                Swal.fire({ icon: 'success', title: 'Created!', timer: 800, showConfirmButton: false })
                    .then(() => navigate('/admin/dashboard/gift-cards'));
            }
        },
        onError: err => {
            const msg = err?.response?.data?.message || 'Could not create the gift card.';
            Swal.fire('Error', msg, 'error');
        },
    });

    const addTag    = slug => { if (!selectedTags.includes(slug)) setSelectedTags(p => [...p, slug]); setMenuOpen(false); };
    const removeTag = slug => setSelectedTags(p => p.filter(s => s !== slug));
    const available = categories.filter(c => !selectedTags.includes(c.slug));

    return (
        <>
            <Navbar title="Gift Cards" />

            <div className="gf-page">
                {/* ── Header ── */}
                <div className="gf-header">
                    <div className="gf-header-left">
                        <Link to="/admin/dashboard/gift-cards" className="gf-back-link">
                            <i className="fa-solid fa-arrow-left"></i> Back
                        </Link>
                    </div>
                    <button
                        className="gf-save-btn"
                        onClick={handleSubmit(data => mutate(data))}
                        disabled={saving}
                    >
                        {saving ? 'Saving…' : 'Save Card'}
                    </button>
                </div>

                {/* ── Panel ── */}
                <div className="gf-panel">
                    <div className="gf-two-col">

                        {/* Left — image */}
                        <div className="gf-img-col">
                            <p className="gf-col-heading">Card Image</p>

                            <label className={`gf-upload-label${imagePreview ? ' has-img' : ''}`}>
                                <input type="file" accept=".png,.jpg,.jpeg" className="gf-upload-input" onChange={handleFile} />

                                {imagePreview ? (
                                    <>
                                        <img className="gf-upload-preview" src={imagePreview} alt="Preview" />
                                        <div className="gf-upload-overlay">
                                            <i className="fa-solid fa-camera" style={{ fontSize: '1.3rem', color: '#fff' }}></i>
                                            <span className="gf-upload-change">Change image</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="gf-upload-ico"><i className="fa-regular fa-image"></i></div>
                                        <span className="gf-upload-primary">Drop image here</span>
                                        <span className="gf-upload-secondary">or click to browse<br />PNG · JPG · up to 10 MB</span>
                                    </>
                                )}
                            </label>

                            <p className="gf-img-note">Recommended: 1920 × 1080 px or larger for best display quality.</p>
                        </div>

                        {/* Right — fields */}
                        <div className="gf-fields-col">
                            <div className="gf-grid">

                                {/* Title */}
                                <div className="gf-field full">
                                    <label className="gf-label">Title</label>
                                    <input className="gf-input" placeholder="e.g. Tillykke" {...register('title')} />
                                </div>

                                {/* Price */}
                                <div className="gf-field">
                                    <label className="gf-label">Price</label>
                                    <input className="gf-input" type="number" placeholder="30" {...register('price')} />
                                </div>

                                {/* Currency */}
                                <div className="gf-field">
                                    <label className="gf-label">Currency</label>
                                    <div className="gf-select-wrap">
                                        <select
                                            className="gf-select"
                                            value={currency}
                                            {...register('currency_code')}
                                            onChange={e => setValue('currency_code', e.target.value)}
                                        >
                                            <option value="DKK">DKK — Danish Krone</option>
                                            <option value="EUR">EUR — Euro</option>
                                        </select>
                                        <i className="fa-solid fa-chevron-down gf-select-chevron"></i>
                                    </div>
                                </div>

                                {/* Language */}
                                <div className="gf-field">
                                    <label className="gf-label">Language</label>
                                    <div className="gf-select-wrap">
                                        <select
                                            className="gf-select"
                                            value={language}
                                            {...register('language_code')}
                                            onChange={e => setValue('language_code', e.target.value)}
                                        >
                                            <option value="da">Danish</option>
                                            <option value="en">English</option>
                                        </select>
                                        <i className="fa-solid fa-chevron-down gf-select-chevron"></i>
                                    </div>
                                </div>

                                {/* Status */}
                                <div className="gf-field">
                                    <label className="gf-label">Status</label>
                                    <div className="gf-status-row">
                                        <button
                                            type="button"
                                            className={`gf-status-pill${isActive === 1 ? ' is-active' : ''}`}
                                            onClick={() => setValue('is_active', 1)}
                                        >
                                            <i className="fa-solid fa-circle" style={{ fontSize: '0.45rem' }}></i>
                                            Active
                                        </button>
                                        <button
                                            type="button"
                                            className={`gf-status-pill${isActive === 0 ? ' is-inactive' : ''}`}
                                            onClick={() => setValue('is_active', 0)}
                                        >
                                            <i className="fa-regular fa-circle" style={{ fontSize: '0.45rem' }}></i>
                                            Inactive
                                        </button>
                                    </div>
                                </div>

                                <div className="gf-rule"></div>

                                {/* Tags */}
                                <div className="gf-field full">
                                    <label className="gf-label">Tags</label>
                                    <div className="gf-tags-row">
                                        {selectedTags.map(slug => {
                                            const cat = categories.find(c => c.slug === slug);
                                            const label = cat ? (language === 'da' ? cat.name_da : cat.name_en) : slug;
                                            const col = slugColor(slug);
                                            return (
                                                <span
                                                    key={slug}
                                                    className="gf-chip"
                                                    style={{ background: col.bg, color: col.text, borderColor: col.border }}
                                                >
                                                    {label}
                                                    <button type="button" className="gf-chip-x" onClick={() => removeTag(slug)}>
                                                        <i className="fa-solid fa-xmark"></i>
                                                    </button>
                                                </span>
                                            );
                                        })}

                                        {available.length > 0 && (
                                            <div className="gf-add-tag-wrap" ref={menuRef}>
                                                <button
                                                    type="button"
                                                    className="gf-add-tag-btn"
                                                    onClick={() => setMenuOpen(o => !o)}
                                                >
                                                    <i className="fa-solid fa-plus" style={{ fontSize: '0.6rem' }}></i>
                                                    Add tag
                                                </button>
                                                {menuOpen && (
                                                    <div className="gf-tag-menu">
                                                        {available.map(cat => {
                                                            const col = slugColor(cat.slug);
                                                            return (
                                                                <div
                                                                    key={cat.id}
                                                                    className="gf-tag-option"
                                                                    onClick={() => addTag(cat.slug)}
                                                                >
                                                                    <span className="gf-tag-dot" style={{ background: col.text }}></span>
                                                                    {language === 'da' ? cat.name_da : cat.name_en}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
}